import React, {useEffect, useRef, useState} from "react"
import {CustomUserFieldsFormProps, FormValues} from "@/features/management-v2/custom-user-fields/types";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {FieldRow} from "@/features/management-v2/custom-user-fields/FieldRow";
import {useSubmitCustomUserFields} from "@/hooks/useSubmitCustomUserFields";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";

export function CustomUserFieldsForm({
                                         initialValue = [],
                                         clientID,
                                     }: CustomUserFieldsFormProps) {
    const methods = useForm<FormValues>({
        defaultValues: {
            fields: initialValue
        }
    })

    const formRef = useRef<HTMLFormElement>(null)
    const {control, handleSubmit} = methods

    const [savedKeys, setSavedKeys] = useState<Set<string>>(() => new Set(initialValue.map(f => f.key)))

    const {fields, append, remove} = useFieldArray<FormValues>({
        control,
        name: "fields"
    })

    const watchedFields = methods.watch("fields")
    const sirenIdentifierIndex = watchedFields?.findIndex(f => f.flags?.includes("siren_identifier")) ?? -1

    const { submitFields, loading: submitting, error, success } = useSubmitCustomUserFields(clientID)

    const [successKey, setSuccessKey] = useState(0)
    const [errorKey, setErrorKey] = useState(0)
    const [showSuccess, setShowSuccess] = useState(false)
    const [showError, setShowError] = useState(false)

    useEffect(() => {
        if (success) {
            setSavedKeys(new Set(fields.map(f => f.key)))
            setSuccessKey(k => k + 1)
            setShowSuccess(true)
            const t = setTimeout(() => setShowSuccess(false), 4000)
            return () => clearTimeout(t)
        }
    }, [success])

    useEffect(() => {
        if (error) {
            setErrorKey(k => k + 1)
            setShowError(true)
            const t = setTimeout(() => setShowError(false), 4000)
            return () => clearTimeout(t)
        }
    }, [error])

    const onSubmit = async (data: FormValues) => {
        await submitFields(data.fields)
    }

    return (
        <FormProvider {...methods}>
            <form
                ref={formRef}
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
            >
                <div className="flex flex-col gap-4">
                    {fields.map((field, index) => (
                        <FieldRow
                            key={field.id}
                            index={index}
                            remove={remove}
                            isExisting={savedKeys.has(field.key)}
                            sirenIdentifierTaken={sirenIdentifierIndex !== -1 && sirenIdentifierIndex !== index}
                        />
                    ))}
                </div>

                <div className="flex flex-col gap-4">
                    <button
                        type="button"
                        onClick={() =>
                            append({
                                name: "",
                                key: "",
                                type: "text",
                                flags: []
                            })
                        }
                        className="px-4 py-2 rounded-lg bg-desaturated-grey border border-light-light-grey hover:bg-ice transition"
                    >
                        + Add Field
                    </button>

                    {showSuccess && (
                        <div key={successKey} className="animate-fadeOut px-4 py-3 rounded-lg bg-desaturated-green border border-green text-dark-green text-sm font-medium">
                            Custom fields saved successfully.
                        </div>
                    )}
                    {showError && (
                        <div key={errorKey} className="animate-fadeOut px-4 py-3 rounded-lg bg-light-red border border-red text-dark-red text-sm font-medium">
                            {error}
                        </div>
                    )}

                    <ButtonV2 label={"Save"} loading={submitting} onClick={handleSubmit(onSubmit)}/>
                </div>
            </form>
        </FormProvider>
    )
}